/**
 * The house style for every chart the sandbox returns.
 *
 * The sandbox emits a bare Plotly figure, so until now the chat rendered stock
 * Plotly: an eleven-icon modebar floating over the plot, the default blue/red
 * qualitative palette, `LE 172,334,878` value labels, a vertically rotated axis
 * title and `150,000,000` tick labels.
 *
 * This is a **floor, not an override**. Everything here is merged UNDER the
 * figure's own layout, per axis as well as at the top level, so a figure that
 * deliberately sets a colour, a margin or a legend keeps it.
 */

export const SIGNALS_LAYOUT: Record<string, any> = {
  font: {
    family: 'Inter, "IBM Plex Sans Arabic", sans-serif',
    size: 12,
    color: '#3E4C59'
  },
  // Two periods of one measure are one hue at two lightnesses. Blue vs red
  // would read as good vs bad, and last year is not bad.
  colorway: ['#0080E0', '#BCD8F4', '#6E9FF7', '#04101A', '#8FBFEA'],
  paper_bgcolor: 'transparent',
  plot_bgcolor: 'transparent',
  margin: { l: 48, r: 12, t: 8, b: 40 },
  showlegend: false, // the card header carries the legend
  bargap: 0.42,
  bargroupgap: 0.12,
  hoverlabel: {
    bgcolor: '#04101A',
    bordercolor: '#04101A',
    font: {
      color: '#FFFFFF',
      size: 12,
      family: 'Inter, "IBM Plex Sans Arabic", sans-serif'
    }
  },
  xaxis: {
    showgrid: false,
    zeroline: false,
    showline: true,
    linecolor: '#DCE4EB',
    ticks: '',
    tickfont: { size: 12, color: '#3E4C59' },
    title: { text: '' }
  },
  yaxis: {
    showgrid: true,
    gridcolor: '#EDF1F5',
    zeroline: false,
    showline: false,
    ticks: '',
    tickfont: { size: 11, color: '#9BAAB8' },
    // `~s` is what turns 150,000,000 into 150M.
    tickformat: '~s',
    separatethousands: true,
    title: { text: '' }
  }
};

export const SIGNALS_CONFIG: Record<string, any> = {
  displayModeBar: false,
  displaylogo: false,
  responsive: true
};

const isPlainObject = (v: any): v is Record<string, any> =>
  !!v && typeof v === 'object' && !Array.isArray(v);

/**
 * Deep merge where `over` always wins. Arrays are replaced, not merged: a
 * figure that supplies its own `colorway` means that list, not that list
 * concatenated with ours.
 */
const mergeUnder = (base: any, over: any): any => {
  if (over === undefined) return base;
  if (!isPlainObject(base) || !isPlainObject(over)) return over;
  const out: Record<string, any> = { ...base };
  for (const key of Object.keys(over)) {
    out[key] = isPlainObject(base[key])
      ? mergeUnder(base[key], over[key])
      : over[key];
  }
  return out;
};

/** Every axis key a figure might carry — `xaxis`, `yaxis`, `xaxis2`, … */
const AXIS_RE = /^[xy]axis\d*$/;

/**
 * Applies the house layout under a figure's own.
 *
 * Secondary axes matter: merging only `xaxis`/`yaxis` leaves a figure's
 * `yaxis2` with stock Plotly styling beside a themed `yaxis`, which looks like
 * a rendering bug rather than a choice.
 */
export const applySignalsLayout = (layout: any): any => {
  const merged = mergeUnder(SIGNALS_LAYOUT, layout || {});

  for (const key of Object.keys(merged)) {
    if (!AXIS_RE.test(key)) continue;
    const template = key.startsWith('x')
      ? SIGNALS_LAYOUT.xaxis
      : SIGNALS_LAYOUT.yaxis;
    merged[key] = mergeUnder(template, (layout || {})[key] || {});
  }

  // The card header owns the title. A figure title would sit inside the plot
  // area and collide with the header directly above it.
  if (merged.title) merged.title = { ...merged.title, text: '' };

  return merged;
};

// A month, a date, a quarter or a year: '2026-04', '2026-04-30',
// '2026-04-30T00:00:00', '2026-Q2', '2026'. ISO-shaped labels sort correctly
// as plain strings, which is what `category ascending` relies on.
const TIME_LABEL_RE =
  /^(19|20)\d{2}(-(\d{2}|Q[1-4])(-\d{2})?([T ][\d:.]+Z?)?)?$/;

const isTimeLabel = (v: any): boolean =>
  (typeof v === 'string' && TIME_LABEL_RE.test(v.trim())) ||
  (typeof v === 'number' && Number.isInteger(v) && v >= 1900 && v <= 2100) ||
  v instanceof Date;

/**
 * Time always runs oldest-left, newest-right — Arabic answers included.
 *
 * The sandbox prompt tells the model to set `xaxis.autorange = 'reversed'` for
 * an Arabic answer so that categories read right to left. Applied to months
 * that puts September on the left and April on the right: the newest period
 * first, which is how a customer read it as "sales are falling" on a chart
 * that was rising. A chart of branches or brands may still read right to left;
 * a time axis may not, whoever emitted it and whenever it was stored.
 *
 * Only vertical traces count (on a horizontal bar the x axis is the value),
 * and only when EVERY label on the axis is a time label.
 */
export const normaliseTimeAxes = (layout: any, data: any[]): any => {
  if (!layout || !Array.isArray(data)) return layout;
  const labels: Record<string, any[]> = {};
  for (const trace of data) {
    if (!trace || typeof trace !== 'object' || trace.type === 'pie') continue;
    if (trace.orientation === 'h' || !Array.isArray(trace.x)) continue;
    const ref = typeof trace.xaxis === 'string' ? trace.xaxis : 'x';
    const key = 'xaxis' + ref.slice(1);
    (labels[key] = labels[key] || []).push(...trace.x);
  }
  let out = layout;
  for (const [key, values] of Object.entries(labels)) {
    const present = values.filter((v) => v !== null && v !== undefined);
    const axis = layout[key] || {};
    const timeAxis =
      axis.type === 'date' ||
      (present.length > 0 && present.every(isTimeLabel));
    if (!timeAxis) continue;
    const fixed: Record<string, any> = { ...axis };
    if (fixed.autorange === 'reversed') fixed.autorange = true;
    if (Array.isArray(fixed.range) && fixed.range.length === 2) {
      const [a, b] = fixed.range;
      if (a > b) fixed.range = [b, a];
    }
    // A category axis keeps the data's order; the model does not always emit
    // oldest-first, so sort it. A date axis is ordered by value already.
    if (fixed.type !== 'date' && fixed.categoryorder === undefined) {
      fixed.categoryorder = 'category ascending';
    }
    out = { ...out, [key]: fixed };
  }
  return out;
};

export const applySignalsConfig = (config: any): any =>
  mergeUnder(SIGNALS_CONFIG, config || {});

/** The title the figure wanted, so the card header can show it instead. */
export const figureTitle = (layout: any): string | undefined => {
  const t = layout?.title;
  const text = typeof t === 'string' ? t : t?.text;
  return typeof text === 'string' && text.trim() ? text.trim() : undefined;
};

/**
 * The unit, for the header's subtitle.
 *
 * Taken from an axis title when the figure set one, because that is where the
 * sandbox puts "EGP" today — and where it reads as a vertically rotated word
 * nobody can scan. Stated once in the header it costs nothing and is legible.
 */
export const figureUnit = (layout: any): string | undefined => {
  const candidates = [layout?.yaxis?.title, layout?.xaxis?.title];
  for (const c of candidates) {
    const text = typeof c === 'string' ? c : c?.text;
    if (typeof text === 'string' && text.trim()) return text.trim();
  }
  return undefined;
};

const SCALES: [number, string][] = [
  [1e9, 'B'],
  [1e6, 'M'],
  [1e3, 'K']
];

/** `172334878` -> `172.3M`. The compact form the answers already use. */
export const compact = (value: number): string => {
  if (!Number.isFinite(value)) return '—';
  const abs = Math.abs(value);
  for (const [factor, suffix] of SCALES) {
    if (abs >= factor) {
      // One decimal, but a round number keeps its round look: an axis tick
      // reads `150M` while a value label reads `172.3M`.
      const scaled = (value / factor).toFixed(1).replace(/\.0$/, '');
      return `${scaled}${suffix}`;
    }
  }
  return abs >= 1 ? value.toFixed(0) : String(value);
};

/** Period-over-period change, as a percentage of the older value. */
export const changePct = (current: number, previous: number): number | null => {
  if (!Number.isFinite(current) || !Number.isFinite(previous)) return null;
  // A change from zero has no percentage. Reporting "Up 100%" on a base of
  // nothing is how a card once claimed returns had risen when they had gone to
  // zero, so this refuses rather than inventing a denominator.
  if (previous === 0) return null;
  return ((current - previous) / Math.abs(previous)) * 100;
};

/**
 * Trace-level defaults.
 *
 * Plotly's pie default puts every label outside on a leader line. With twelve
 * slices that becomes a column of text taller than the chart, the donut
 * squeezed into whatever is left, and slices under 1% labelled to three decimal
 * places. A pie with that many parts should not be drawn at all — the prompt
 * now says so — but when one arrives it has to stay readable.
 *
 * Only fills gaps: anything the figure states explicitly is kept.
 */
export const applySignalsTraces = (data: any[]): any[] => {
  if (!Array.isArray(data)) return data;
  return data.map((trace) => {
    if (!trace || typeof trace !== 'object') return trace;
    if (trace.type !== 'pie') return trace;

    const out: any = { ...trace };
    // Labels inside the slices, so the chart is the chart rather than a
    // legend with a picture next to it.
    if (out.textposition === undefined) out.textposition = 'inside';
    if (out.insidetextorientation === undefined)
      out.insidetextorientation = 'horizontal';
    // Percent only: the name is already on the slice's hover and in the card's
    // legend, and two lines per slice is what made the column of text.
    if (out.textinfo === undefined) out.textinfo = 'percent';
    // A slice too small to hold its own label gets none rather than a leader
    // line to the margin.
    if (out.insidetextfont === undefined) out.insidetextfont = { size: 12 };
    if (out.automargin === undefined) out.automargin = true;
    if (out.hovertemplate === undefined)
      out.hovertemplate = '%{label}: %{percent}<extra></extra>';
    return out;
  });
};
